"use client";

import { useState } from "react";

interface GenreLayer {
  layer: string;
  genre: string;
  description: string;
}

const layerIcons: Record<string, string> = {
  Vocals: "🎤",
  "Lead Instrument": "🎸",
  "Melody Layer": "🎹",
  Drums: "🥁",
  Percussion: "🪘",
  Pads: "🎛️",
  Bass: "🎵",
};

export default function GenreGeneratorPage() {
  const [layers, setLayers] = useState<GenreLayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/tools/genre-generator", { method: "POST" });
      if (res.status === 429) {
        setError("Rate limit reached. Please wait a few minutes and try again.");
        return;
      }
      if (!res.ok) throw new Error("Failed to generate");
      const data = await res.json();
      setLayers(data.layers);
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-base-100 p-4 sm:p-6 flex flex-col items-center"
    >
      <div className="w-full max-w-2xl">
        <h2 className="text-2xl font-bold text-base-content text-center mb-1">
          Music Genre Generator
        </h2>
        <p className="text-base-content/60 text-center text-sm mb-5">
          AI-powered 7-layer genre blueprint for your next track
        </p>

        <div className="flex justify-center mb-6">
          <button
            className={`btn btn-primary btn-wide ${loading ? "loading" : ""}`}
            onClick={generate}
            disabled={loading}
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : null}
            {loading
              ? "Generating..."
              : layers.length
                ? "Generate Again"
                : "Generate Genres"}
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {layers.length > 0 && (
          <div className="space-y-3">
            {layers.map((item) => (
              <div
                key={item.layer}
                className="card bg-base-200 border border-base-300"
              >
                <div className="card-body p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">
                      {layerIcons[item.layer] || "🎶"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-base-content/50 uppercase tracking-wide">
                          {item.layer}
                        </span>
                        <span className="badge badge-primary badge-sm">
                          {item.genre}
                        </span>
                      </div>
                      <p className="text-sm text-base-content/70">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!layers.length && !loading && (
          <div className="text-center py-12 text-base-content/30">
            <p className="text-4xl mb-3">🎲</p>
            <p>Press the button to generate a unique genre combination</p>
          </div>
        )}
      </div>
    </div>
  );
}
